import React, { useState, useContext, useEffect } from 'react'
import SlideToggle from "react-slide-toggle";
import { getFormattedDate } from '../helpers/formattedDate';
import FutureCourseInstance from './futureCourseInstance';
import LoadingPanel from './loadingPanel';
import { getOpenSeats, getShortestWaitlistCourseId } from "../helpers/coursesHelper";
import FutureOnlineCourseInstance from './futureOnlineCourseInstance';

//import icons
import PlayButton from '../../../../../img/button-play-circle.svg';

//import redux dependencies
import { useSelector } from 'react-redux'

//import api calls
import { useNoOpenSeatsQuery } from '../api/messages';

// import context
import { Context } from '../courseTemplates';

/**
 * Future courses component
 * @param {Object} courseInstances - Future course instances data 
 * @param {number} courseRecordId - Course template method record id
 * @param {string} highestEligibleCategory - Users highest category
 * @param {Function} futureCoursesRefetch - Refetch future courses for this course record id - used for the future course instance component on accordion expand
 */
function FutureCourses({ courseInstances, courseRecordId, highestEligibleCategory, futureCoursesRefetch, onlineCourseRecordId, onlineOnly }) {
    // get our global context variables
    const value = useContext(Context);
    const { loggedIn } = value;

    // get the current active course name from the redux store
    const activeCourseName = useSelector((state) => state.activeCourseName.value)

    // make our api calls for the messages
    const { data: noOpenSeatsData } = useNoOpenSeatsQuery();


    // Filter showing course instances with open seats
    const [filterOpenSeats, setFilterOpenSeats] = useState(false);
    const [instanceDataArray, setInstanceDataArray] = useState([]);
    const [collapseEvent, setCollapseEvent] = useState(0);


    // when the active course name changes (swiper swipes), reset the collapse event to close all accordions
    // this is kind of a workaround for accordion being left open in a half rendered state when the user swipes
    useEffect(() => {
        setCollapseEvent(Date.now());
    },[activeCourseName]);


    // get the shortest waitlist course post id
    const shortestWaitlistCourseId = getShortestWaitlistCourseId(courseInstances, highestEligibleCategory)


    // click handler for the open seats filter
    const clickOpenSeatsFilter = () => {
        setFilterOpenSeats(!filterOpenSeats);
      };

    /**
     * Get center column data for accordion header
     * @param {Object} courseInstance - Details for the current category row
     * @param {number} openSeats - Number of open seats
     * @returns JSX
     */
    const getCenterColumn = (courseInstance, openSeats) => {
        if(!loggedIn){
            return (<></>);
        // if registration is closed
        } else if (!courseInstance.Timber_Post.registration_open || courseInstance.Timber_Post.registration_open == "false"){
            return (<div className="seats-text rg-5 rg-sm-12 rg-xs-12">Registration closed</div>)
        // if there are open seats and registration is open
        } else if(openSeats > 0){
            return(<div className="seats-text rg-5 rg-sm-12 rg-xs-12">{openSeats} Open Seats</div>)
        // if there are no open seats and registration is open and this course has the shortest waitlist
        } else if (shortestWaitlistCourseId === courseInstance.Timber_Post.ID){
            return(<div className="seats-text rg-5 rg-sm-12 rg-xs-12">Shortest waitlist</div>)
        }
    }

    /**
     * Get an expanded accordion row
     * @param {Object} courseInstance - Details for the current category row
     * @param {number} index - Row and course index, mostly used for jump links from the upcoming dates ribbon component
     * @param {boolean} forceReturn - Flag to force return of the accordion row, skips the open seats check. Used for the shortest waitlist course when no open seats are available and the filter is on
     * @returns JSX
     */
        const accordionRow = (courseInstance, index, forceReturn = false) => {
            // if we are filtering by open seats and the course instance has no open seats  skip it
            let openSeats = getOpenSeats(courseInstance, highestEligibleCategory);
            if(highestEligibleCategory === "Clinical Instructor"){
                openSeats += getOpenSeats(courseInstance, "Mentor");
            }

            if(filterOpenSeats && (openSeats <= 0 || !courseInstance.Timber_Post.registration_open || courseInstance.Timber_Post.registration_open == "false") && forceReturn === false){
                return;
            }

            // return the JSX for the accordion row
            return(
                <SlideToggle 
                    collapsed="true" 
                    onExpanded={({ hasReversed }) => {
                        setInstanceDataArray([...instanceDataArray, courseInstance.Timber_Post.method_record_id]);
                    }}
                    collapseEvent={collapseEvent}
                >
                    {({ toggle, toggleState, setCollapsibleElement }) => (
                        
                        <>
                            {/* Table row header (accordion title) */}
                            <dt className={(toggleState === "COLLAPSED" || toggleState === "COLLAPSING")  ? '' : 'active'} onClick={toggle}>
                                <h4 className="tab-title h2 responsive-grid rg-align-items-center">
                                    <a className="rg-5 rg-sm-12 rg-xs-12" id={'course' + index} >{getFormattedDate(courseInstance.Timber_Post.course_start_timestamp, courseInstance.Timber_Post.course_end_timestamp)}  Course</a>
                                    {getCenterColumn(courseInstance, openSeats)}
                                </h4>
                                
                            </dt>

                            {/* Table row content (accordion body) */}
                            <dd ref={setCollapsibleElement}>
                                <div className="table-course-list">
                                    {/* If the user isn't logged in inform them they need to login */}
                                    {!loggedIn && 
                                        <div className="table-course-list__row responsive-grid">
                                            <div className="rg-5 rg-sm-12 rg-xs-12">
                                                <h4 className="cell-headline">You need to log in to sign up</h4>
                                            </div>
                                            <div className="rg-3 rg-sm-12 rg-xs-12">
                                                <div className="headline-label headline-label--txt-color"></div>
                                            </div>
                                            <div className="rg-4 rg-sm-12 rg-xs-12">
                                                <a href="/login" className="btn c-btn--light">Login</a>
                                            </div>
                                        </div>
                                    }
                                    {/* if the user is logged in */}
                                    {/* set loading state initially */}
                                    { loggedIn && !instanceDataArray.includes(courseInstance.Timber_Post.method_record_id) &&
                                    // this loading panel is here as a "skeleton" so that the accordion knows to slide down, something needs to be in the body
                                    // or else it pops in weird, this is a work around. The animation is paused so that when the loader from the future course instance
                                    // component loads in it doesn't reset the animation causing a flicker
                                        <LoadingPanel loadText="Loading Course Details" paused={true}/>
                                    }
                                    {/* once loaded show the instances */}
                                    { loggedIn && instanceDataArray.includes(courseInstance.Timber_Post.method_record_id) &&
                                        <FutureCourseInstance instanceId={courseInstance.Timber_Post.method_record_id} courseRecordId={courseRecordId} futureCoursesRefetch={futureCoursesRefetch}/>
                                    }
                                </div>
                            </dd>
                        </>
                    )}
                </SlideToggle>
            )
        }

        /**
     * Get the online course accordion row
     * @returns JSX
     */
    const getOnlineCourseRow = () => {
        // return the JSX for the accordion row
        return(
            <SlideToggle 
                collapsed="true" 
                onExpanded={({ hasReversed }) => {
                    //getFutureInstances({courseRecordId, instanceId: courseInstance.Timber_Post.method_record_id});
                    setInstanceDataArray([...instanceDataArray, 'online']);
                }}
            >
                {({ toggle, toggleState, setCollapsibleElement }) => (
                    <>
                        {/* Table row header (accordion title) */}
                        <dt id="online-course-anchor" className={(toggleState === "COLLAPSED" || toggleState === "COLLAPSING")  ? '' : 'active'} onClick={toggle}>
                            <h4 className="tab-title h2 responsive-grid rg-align-items-center">
                                <a className="rg-5 rg-sm-12 rg-xs-12"id="online-course-accordion-title" ><span><PlayButton/></span>Standard Digital Course</a>
                            </h4>
                        </dt>

                        {/* Table row content (accordion body) */}
                        <dd ref={setCollapsibleElement}>
                            <div className="table-course-list">
                                {/* If the user isn't logged in inform them they need to login */}
                                {!loggedIn && 
                                    <div className="table-course-list__row responsive-grid">
                                        <div className="rg-5 rg-sm-12 rg-xs-12">
                                            <h4 className="cell-headline">You need to log in to sign up</h4>
                                        </div>
                                        <div className="rg-3 rg-sm-12 rg-xs-12">
                                            <div className="headline-label headline-label--txt-color"></div>
                                        </div>
                                        <div className="rg-4 rg-sm-12 rg-xs-12">
                                            <a href="/login" className="btn c-btn--light">Login</a>
                                        </div>
                                    </div>
                                }
                                {/* if the user is logged in */}
                                {/* set loading state initially */}
                                { loggedIn && !instanceDataArray.includes('online') &&
                                // this loading panel is here as a "skeleton" so that the accordion knows to slide down, something needs to be in the body
                                // or else it pops in weird, this is a work around. The animation is paused so that when the loader from the future course instance
                                // component loads in it doesn't reset the animation causing a flicker
                                    <LoadingPanel loadText="Loading Online Course Details" paused={true}/>
                                }
                                {/* once loaded show the instances */}
                                { loggedIn && instanceDataArray.includes('online') &&
                                    <FutureOnlineCourseInstance courseRecordId={courseRecordId}/>
                                }
                            </div>
                        </dd>
                    </>
                )}
            </SlideToggle>
        )
    }

        const maybeShowNoOpenSeatsMessage = () => {
            if(checkForOpenSeats()){
                return <></>;
            } else {
                return (
                    <div dangerouslySetInnerHTML={{__html: noOpenSeatsData.message}}></div>
                )
            }
        }

        const maybeShowShortestWaitlistCourse = () => {
            if(checkForOpenSeats()){
                return <></>;
            } else {
                return (
                    <>
                        { courseInstances.map((courseInstance, index) => {
                            if(shortestWaitlistCourseId === courseInstance.Timber_Post.ID){
                                return (accordionRow(courseInstance, index, true))
                            }
                            return (<> </>);
                        })}
                    </>
                )
            }
        }

        const checkForOpenSeats = () => {
            let openSeats = 0;
            courseInstances.map((courseInstance, index) => {
                openSeats += getOpenSeats(courseInstance, highestEligibleCategory);
                if(highestEligibleCategory === "Clinical Instructor"){
                    openSeats += getOpenSeats(courseInstance, "Mentor");
                }
            });
            if(openSeats > 0){
                return true;
            } else {
                return (
                    false
                )
            }
        }

    // Component level JSX return statement ******************************************************
    return (
        <>
            {/* check that we have future courses to show */}
            {(courseInstances.length > 0 || onlineOnly) &&
                <>
                    <div className="pattern-block__headline flex-row col-lg-gap future-courses-container">
                        <h2 id="future-courses" className="border-headline">Future Courses</h2>
                        {/* If user is logged in, display the open seats filter check box */}
                        {loggedIn &&  !onlineOnly &&
                        <div className="open-seats-filter-container">
                            <input type="checkbox" checked={filterOpenSeats}/>
                            <label onClick={clickOpenSeatsFilter}>Only show courses with available seats</label>
                        </div>
                        }
                    </div>
                    {/* If there are no open seats, display the info message */}
                    {filterOpenSeats && !onlineOnly &&
                        maybeShowNoOpenSeatsMessage()
                    }
                    {/* Accordion start */}
                    <dl className="accordion accordion--courses">
                        {/* Loop through courses and get the row JSX */}
                        { courseInstances.map((courseInstance, index) => {
                            return(
                                accordionRow(courseInstance, index)
                            )})
                        }
                        {/* If we have an online course show it */}
                        {onlineCourseRecordId && 
                                <>
                                    {getOnlineCourseRow()}
                                </>
                            }
                        {/* If we go through them all and there's no open seats and we are filtering, show them the course with the
                            shortest waitlist */}
                        {filterOpenSeats && !onlineOnly &&
                            maybeShowShortestWaitlistCourse()
                        }
                    </dl>
                    {/* Accordion End */}
                </>
            }
        </>
    );
}

export default FutureCourses;